import { ReactNode } from "react";
import { ConnectFooter } from "@/components/connect/ConnectFooter";
import { ConnectPortalLayout } from "@/components/layout/ConnectPortalLayout";
import { useAuth } from "@/contexts/AuthContext";
import { useIsNative } from "@/hooks/useIsNative";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { Logo } from "@/components/ui/Logo";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";


/**
 * Wraps legal/info pages so they render inside the in-app portal chrome
 * (bottom nav, header, back-to-account button) when the user is signed in,
 * and inside the marketing Layout for the public web.
 *
 * On native (iOS/Android via Capacitor) for signed-out users - i.e. inside
 * the onboarding flow - we deliberately skip the marketing Navbar/Footer
 * and render a minimal in-app shell with a "Done" close button. This stops
 * legal links from feeling like the user was bounced into the website.
 */
export function LegalShell({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();
  const isNative = useIsNative();
  const navigate = useNavigate();
  const { t } = useTranslation("common");

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (user) {
    return (
      <ConnectPortalLayout requireAuth={false}>
        {children}
      </ConnectPortalLayout>
    );
  }

  if (isNative) {
    return (
      <div className="min-h-[100dvh] bg-background flex flex-col">
        <header
          className="sticky top-0 z-40 flex items-center gap-2 border-b border-border/60 bg-background/95 backdrop-blur px-2"
          style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 8px)", paddingBottom: 8 }}
        >
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
            className="flex items-center gap-1 px-2 py-2 -ml-1 rounded-lg text-primary font-medium active:opacity-60"
            aria-label="Back"
          >
            <ChevronLeft className="h-5 w-5" aria-hidden />
            <span className="text-[15px]">Done</span>
          </button>
        </header>
        <main className="flex-1 overflow-y-auto">{children}</main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <nav className="container pt-2 pb-3 flex items-center justify-between gap-2">
        <Logo size="sm" withIcon fullName asLink />
        <div className="flex items-center gap-2">
          <Link to="/connect-portal/auth">
            <Button variant="ghost" size="sm">{t("logIn")}</Button>
          </Link>
          <Link to="/connect-portal/auth">
            <Button size="sm">{t("getStarted")}</Button>
          </Link>
        </div>
      </nav>
      <main className="flex-1">{children}</main>
      <ConnectFooter />
    </div>
  );
}
